// A direct OBS stream's stamped playlist dates every segment
// (EXT-X-PROGRAM-DATE-TIME), so the player can say what wall-clock moment the
// frame on screen was captured at. The host's music states arrive stamped with
// the server clock; replaying them against the frame clock keeps the viewer's
// music exactly with the picture, whatever their buffer does.

const KEEP_STATES_MS = 120000;

// How the viewer's music follows the picture. The server sends these with
// the join and live updates (skcore services/media/streamConfig.js), so they
// change without a deploy; these defaults match it. Drift is closed by a
// pitch-kept tempo change, seeked only when too large to play out, and held
// while the picture is stalled; seeking on every drift was heard as skipping.
export const DEFAULT_MUSIC_TIMING = Object.freeze({
  musicDriftRateSec: 0.5,
  musicDriftSeekSec: 4,
  musicDriftDoneSec: 0.2,
  musicCatchUpRate: 1.025,
  musicStallMs: 600,
  musicClockOffsetMs: 0,
  musicVolumeScale: 0.3,
  useFrameClock: true,
});
export const CATCH_UP_RATE = DEFAULT_MUSIC_TIMING.musicCatchUpRate;

/** The server's timing values over [current], ignoring anything malformed. */
export function musicTimingFrom(streamConfig, current = DEFAULT_MUSIC_TIMING) {
  if (!streamConfig || typeof streamConfig !== "object") return current;
  const timing = { ...current };
  for (const key of Object.keys(DEFAULT_MUSIC_TIMING)) {
    const value = streamConfig[key];
    if (typeof value === typeof DEFAULT_MUSIC_TIMING[key] && (typeof value !== "number" || Number.isFinite(value))) {
      timing[key] = value;
    }
  }
  return timing;
}

/** Wall-clock time (ms) of the frame on screen, or null while unknown. */
export function readFrameClockMs(hls, video) {
  const playingDate = hls?.playingDate;
  if (playingDate instanceof Date && !Number.isNaN(playingDate.getTime())) return playingDate.getTime();
  // Safari's native player exposes the date of position 0 instead.
  if (video && typeof video.getStartDate === "function") {
    const startDate = video.getStartDate();
    if (startDate instanceof Date && !Number.isNaN(startDate.getTime())) {
      return startDate.getTime() + video.currentTime * 1000;
    }
  }
  return null;
}

/** A host state the viewer keeps: what the host's player did at `atMs`. */
export function hostStateFrom(musicState, atMs) {
  return {
    atMs,
    songs: musicState.songs,
    index: musicState.index,
    progress: typeof musicState.progress === "number" ? musicState.progress : 0,
    isPlaying: musicState.isPlaying === true,
    currentVolume: typeof musicState.currentVolume === "number" ? musicState.currentVolume : null,
  };
}

/** Appends a state, keeping the recent ones in time order. */
// A song the host changes to cannot be further in than the time since the
// previous state; a larger value is the old song's position sent with the
// new song, and would start it mid-way.
function clampSongChanges(sorted) {
  return sorted.map((state, i) => {
    const previous = sorted[i - 1];
    const songId = state.songs?.[state.index]?._id;
    if (!previous || !songId || songId === previous.songs?.[previous.index]?._id) return state;
    const limitSec = (state.atMs - previous.atMs) / 1000 + 1;
    return state.progress > limitSec ? { ...state, progress: limitSec } : state;
  });
}

// Drops states older than two minutes before the newest, except the last of
// them, which stays in force until a newer one: after a gap the frame on
// screen still finds the state that covers it.
function pruneHostStates(sorted) {
  if (!sorted.length) return sorted;
  const cutoff = sorted[sorted.length - 1].atMs - KEEP_STATES_MS;
  const firstKept = sorted.findIndex((state) => state.atMs >= cutoff);
  return firstKept <= 0 ? sorted : sorted.slice(firstKept - 1);
}

/** Adds a state in time order; one at the same moment replaces the known one. */
export function rememberHostState(states, state) {
  const merged = [...states.filter((known) => known.atMs !== state.atMs), state].sort((a, b) => a.atMs - b.atMs);
  return pruneHostStates(clampSongChanges(merged));
}

/** The host state in force at the frame's moment, or null before the first. */
export function hostStateAt(states, frameMs) {
  let latest = null;
  for (const state of states) {
    if (state.atMs > frameMs) break;
    latest = state;
  }
  return latest;
}

/** Where the host's song was at the frame's moment. */
export function expectedHostPositionSec(state, frameMs) {
  const elapsedSec = state.isPlaying ? Math.max(0, (frameMs - state.atMs) / 1000) : 0;
  return Math.max(0, state.progress + elapsedSec);
}

/**
 * How the music should close `driftSec` (positive = music ahead of the
 * picture) given the tempo it currently plays at.
 */
export function planDriftCorrection({ driftSec, stalled = false, rate = 1, timing = DEFAULT_MUSIC_TIMING }) {
  if (stalled) return { action: "hold" };
  const size = Math.abs(driftSec);
  if (size > timing.musicDriftSeekSec) return { action: "seek" };
  if (size > timing.musicDriftRateSec) {
    return { action: "rate", rate: driftSec > 0 ? 2 - timing.musicCatchUpRate : timing.musicCatchUpRate };
  }
  // Caught up, or the tempo now pushes the music the wrong way.
  if (rate !== 1 && (size <= timing.musicDriftDoneSec || (rate > 1) === (driftSec > 0))) return { action: "rate", rate: 1 };
  return { action: "none" };
}

/**
 * States a joining viewer gets from the server (`hostMusicTimeline` in the
 * join payload), in the shape the clock replays.
 */
export function hostStatesFromTimeline(timeline) {
  if (!timeline || !Array.isArray(timeline.songs) || !Array.isArray(timeline.states)) return [];
  return timeline.states
    .filter((state) => typeof state.at === "number")
    .map((state) => ({
      atMs: state.at,
      songs: timeline.songs,
      index: timeline.songs.findIndex((song) => String(song._id) === String(state.songId)),
      progress: typeof state.progress === "number" ? state.progress : 0,
      isPlaying: state.isPlaying === true,
      currentVolume: typeof state.currentVolume === "number" ? state.currentVolume : null,
    }))
    .sort((a, b) => a.atMs - b.atMs);
}

/** Known states plus seeded ones, in time order, one per moment; known ones win. */
export function mergeHostStates(known, seeded) {
  const byMoment = new Map();
  for (const state of [...seeded, ...known]) byMoment.set(state.atMs, state);
  return pruneHostStates(clampSongChanges([...byMoment.values()].sort((a, b) => a.atMs - b.atMs)));
}
